import dayjs from '@skynet/formatting-date';
import { isOkCommand, parseResponse } from '@skynet/data-ws-core';

import { createAppAsyncThunk } from 'src/store/utils/thunks';
import { actionOnMessage } from '@skynet/data-ws-mm';

import { startAppListening } from 'src/store/middleware/listenerMiddleware';
import {
  selectComparisonEndDate,
  selectComparisonStartDate,
} from 'src/store/shared/balance-history/selectors';
import type { BalanceHistoryState, PayloadGetBalanceHistory } from './types';
import { BalanceHistoryCommands } from './types';
import { getBalanceHistoryWs, resetInitialBalance, setInitialBalance } from './actions';
import { initialEndDate, initialStartDate } from './utils';
import { initialBalanceHistorySlice } from 'src/store/shared/balance-history/factory';

const allowedCommands = Object.values(BalanceHistoryCommands);

export const initialState: BalanceHistoryState = {
  requestStatus: null,
  error: null,

  priceHistory: {},
  prices: {},
  regular: {},
  overrides: {},
  initialBalances: {},

  startDate: initialStartDate,
  startTime: '00:00',
  endDate: initialEndDate,
  endTime: 'current',

  // set initial balance
  isInitialBalanceModalOpen: false,
  formSetInitialBalance: undefined,
  resSetInitialBalance: null,
};

const BalanceHistorySlice = initialBalanceHistorySlice({
  prefix: 'shared',
  sliceName: 'balanceHistory',
  commands: BalanceHistoryCommands,
  messages: actionOnMessage,
  initialState,
});

// Action is set here, but not in the actions module to avoid circular dependencies
const getBalanceHistory = createAppAsyncThunk(
  'retainers/GetBalanceHistory',
  (data: PayloadGetBalanceHistory, { getState, dispatch }) => {
    const state = getState();
    const storeEndDate = selectComparisonEndDate(state);
    const storeStartDate = selectComparisonStartDate(state);

    const { startDate, endDate } = data;

    // Update comparison range if it has changed
    if (startDate && endDate && (startDate !== storeStartDate || endDate !== storeEndDate)) {
      dispatch(
        BalanceHistorySlice.actions.setComparisonRange({
          startDate,
          endDate,
        }),
      );
    }

    // If only end date time has changed, do not request history data as we already have the latest snapshot loaded.
    if (
      dayjs(storeEndDate).startOf('day').isSame(dayjs(endDate).startOf('day')) &&
      startDate === storeStartDate
    ) {
      return;
    }

    const payload = {
      ...(data.loanID && { loanID: data.loanID }),
      ...(data.projectID && { projectID: data.projectID }),
      startDate: startDate || storeStartDate || initialStartDate,
      endDate: endDate || storeEndDate || initialEndDate,
    };
    dispatch(getBalanceHistoryWs(payload));
  },
);

export const balanceHistoryActions = {
  ...BalanceHistorySlice.actions,
  getBalanceHistory,
  setInitialBalance,
  resetInitialBalance,
};
// export const { toggleSidebar } = BalanceHistorySlice.actions

export default BalanceHistorySlice.reducer;

// on set initial balance submit
// it should request history list with the same params
startAppListening({
  actionCreator: actionOnMessage,
  effect: async (action, listenerApi) => {
    const { json, skipProcessing } = parseResponse(action.payload, allowedCommands);

    if (skipProcessing || !json) return;

    const { command, okCommand } = json;

    if (isOkCommand(command) && okCommand === BalanceHistoryCommands.SetInitialBalance) {
      const state = listenerApi.getState();
      const { balanceHistory, loans } = state;

      if (!loans.index.targetLoan) return;

      await listenerApi.dispatch(
        getBalanceHistory({
          loanID: +loans.index.targetLoan,
          startDate: balanceHistory.startDate ?? '',
          endDate: balanceHistory.endDate ?? '',
        }),
      );
    }
  },
});
